import { useMediaQuery } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { ApplicationLimitTable } from "@/components/model/user/ApplicationLimitTable";
import { IncentiveTable } from "@/components/model/user/IncentiveTable";
import { PenaltyTable } from "@/components/model/user/PenaltyTable";
import { PointSystemExplanation } from "@/components/model/user/PointSystemExplanation";
import {
  Button,
  Checkbox,
  Divider,
  Group,
  H2,
  Modal,
  Paper,
  Stack,
  Text,
} from "@/components/ui/elements";

type CancelPolicyProps = {
  onConfirmNext?: () => void;
  opened?: boolean;
  onRequestClose?: () => void;
  onRequestOpen?: () => void;
};

export function CancelPolicy({
  onConfirmNext,
  opened: controlledOpened,
  onRequestClose,
  onRequestOpen,
}: CancelPolicyProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [policyConfirmed, setPolicyConfirmed] = useState(false);

  const isOpen = Boolean(controlledOpened);
  const openModal = onRequestOpen ?? (() => {});
  const closeModal = onRequestClose ?? (() => {});

  useEffect(() => {
    if (isOpen) {
      setPolicyConfirmed(false);
    }
  }, [isOpen]);

  const handleModalClose = () => {
    closeModal();
  };

  return (
    <Stack py="0" gap={isMobile ? "sm" : "md"}>
      <H2 mt={0} fz={isMobile ? "lg" : "xl"}>
        キャンセルポリシー
      </H2>
      <Text c="dimmed" fz={isMobile ? "xs" : "sm"}>
        キャンセルポリシーを必ずご確認ください。
      </Text>
      <Divider />

      <Paper
        p={isMobile ? "sm" : "md"}
        radius="md"
        bg="red.0"
        style={{
          border: "1px solid var(--mantine-color-red-2)",
        }}
      >
        <Stack gap={isMobile ? "xs" : "sm"}>
          <Group gap="xs" align="flex-start" wrap="nowrap">
            <Text c="red" fw="bold" fz={isMobile ? "md" : "lg"}>
              ⚠️
            </Text>
            <Text fw="bold" c="red" fz={isMobile ? "sm" : "md"}>
              キャンセル時の影響について
            </Text>
          </Group>

          <Stack gap={isMobile ? "xs" : "xs"} pl={isMobile ? "md" : "xl"}>
            <Group gap="xs" align="flex-start" wrap="nowrap">
              <Text c="red" fw="bold" fz={isMobile ? "xs" : "sm"}>
                •
              </Text>
              <Text fz={isMobile ? "xs" : "sm"} c="dark">
                <Text span fw="bold" c="red">
                  キャンセルは原則禁止
                </Text>
                です
                <br />
                やむを得ない事情がある場合にのみキャンセルを行ってください
              </Text>
            </Group>

            <Group gap="xs" align="flex-start" wrap="nowrap">
              <Text c="red" fw="bold" fz={isMobile ? "xs" : "sm"}>
                •
              </Text>
              <Text fz={isMobile ? "xs" : "sm"} c="dark">
                <Text span fw="bold" c="red">
                  持ち点が減少
                </Text>
                し、
                <Text span fw="bold" c="red">
                  応募制限
                </Text>
                となる可能性があります
                <br />
                新規のお仕事に応募できなくなる場合があります
                <br />
                キャンセル率など利用状況によっては、応募できる件数がさらに制限される場合があります
              </Text>
            </Group>

            <Group gap="xs" align="flex-start" wrap="nowrap">
              <Text c="red" fw="bold" fz={isMobile ? "xs" : "sm"}>
                •
              </Text>
              <Text fz={isMobile ? "xs" : "sm"} c="dark">
                キャンセル履歴は
                <Text span fw="bold" c="red">
                  施設様に公開
                </Text>
                されます
                <br />
                キャンセル率が高い場合は
                <Text span fw="bold" c="red">
                  採用をお断り
                </Text>
                されることがあります
              </Text>
            </Group>

            <Group gap="xs" align="flex-start" wrap="nowrap">
              <Text c="red" fw="bold" fz={isMobile ? "xs" : "sm"}>
                •
              </Text>
              <Text fz={isMobile ? "xs" : "sm"} c="dark">
                <Text span fw="bold" c="red">
                  無断欠勤は利用停止
                </Text>
                となります
                <br />
                損害が発生した場合は
                <Text span fw="bold" c="red">
                  損害賠償
                </Text>
                が発生する可能性があります
              </Text>
            </Group>
          </Stack>
        </Stack>
      </Paper>

      <Stack gap="md">
        <Paper
          p={isMobile ? "sm" : "md"}
          radius="md"
          bg="orange.0"
          style={{
            border: "2px solid var(--mantine-color-orange-3)",
            textAlign: "center",
          }}
        >
          <Stack gap="sm">
            <Text fw="bold" c={"orange"} fz={isMobile ? "sm" : "md"}>
              ⚠️ キャンセルポリシーの確認が必要です
            </Text>
            <Button
              color="teal"
              onClick={openModal}
              size={isMobile ? "md" : "lg"}
              style={{
                width: "100%",
              }}
            >
              キャンセルポリシーを確認する
            </Button>
          </Stack>
        </Paper>
      </Stack>

      <Modal
        opened={isOpen}
        close={handleModalClose}
        title="📋 キャンセルポリシー詳細"
        size={isMobile ? "full" : "lg"}
        fullScreen={isMobile}
      >
        <Stack
          gap={isMobile ? "sm" : "md"}
          style={{
            display: "flex",
            flexDirection: "column",
            maxHeight: isMobile ? "calc(100dvh - 80px)" : "70vh",
          }}
        >
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              paddingRight: isMobile ? 4 : 8,
            }}
          >
            <Stack gap="md">
              <Paper
                p={isMobile ? "sm" : "md"}
                radius="md"
                bg="red.0"
                style={{
                  border: "1px solid var(--mantine-color-red-2)",
                }}
              >
                <Text fw="bold" c="red" fz={isMobile ? "sm" : "md"} mb="sm">
                  ⚠️ 重要な注意事項
                </Text>
                <Stack gap="xs">
                  <Text fz={isMobile ? "xs" : "sm"} c="dark">
                    ・キャンセルは原則禁止です。やむを得ない事情がある場合にのみキャンセルを行ってください。
                  </Text>
                  <Text fz={isMobile ? "xs" : "sm"} c="dark">
                    ・キャンセルが多くなるとペナルティが発生することがあります。
                  </Text>
                  <Text fz={isMobile ? "xs" : "sm"} c="dark" fw="bold">
                    ・日付・曜日・施設の間違いによるキャンセルもペナルティの対象となります。
                  </Text>
                </Stack>
              </Paper>

              <Paper
                p={isMobile ? "sm" : "md"}
                radius="md"
                bg="blue.0"
                style={{
                  border: "1px solid var(--mantine-color-blue-2)",
                }}
              >
                <Text fw="bold" c="blue" fz={isMobile ? "sm" : "md"} mb="sm">
                  💡 基本方針
                </Text>
                <Text fz={isMobile ? "xs" : "sm"} c="dark">
                  キャンセル履歴は施設様に公開されます。キャンセル数が多い、キャンセル率が高い場合、施設様の判断で採用中止となる可能性があります。
                </Text>
                <Text fz={isMobile ? "xs" : "sm"} c="dark" mt="xs">
                  また、本勤務は施設様職員構成を含め、ご利用者様のため、採用確定後にキャンセルした場合にペナルティだけでなく損害賠償が発生する可能性があります。
                </Text>
                <Text fz={isMobile ? "xs" : "sm"} c="dark" mt="xs" fw="bold">
                  以下を必ずご確認いただき、制度をご理解の上応募の申し込みをお願い致します。
                </Text>
              </Paper>

              <PointSystemExplanation />
              <PenaltyTable />
              <IncentiveTable />
              <ApplicationLimitTable />
            </Stack>
          </div>

          <Divider />
          <Checkbox
            checked={policyConfirmed}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPolicyConfirmed(e.currentTarget.checked)
            }
            label="上記の内容を理解し、キャンセルポリシーを確認しました"
            mb="sm"
          />
          <Group gap="sm" justify="center">
            <Button variant="white" onClick={handleModalClose}>
              閉じる
            </Button>
            <Button
              onClick={() => {
                closeModal();
                onConfirmNext?.();
              }}
              disabled={!policyConfirmed}
            >
              確認して次へ
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
